import React, { useState } from 'react';
import MobileModal from '../../../../components/compositions/MobileModal/MobileModal';
import strings from '../../../../constants/strings.constants';
import { useQuery } from 'react-query';
import useAuthStore from '../../../../store/useAuthStore';
import moment from 'moment';
import { getEarlyAvailableDateWhenDisabled } from '../../../../utils/utils';
import useAlertStore from '../../../../store/useAlertStore';
import AppError from '../../../../apis/models/common/res/AppError';
import {
  fetchAvailableReservationDates,
  fetchAvailableReservationTimes,
} from '../../../../apis/partner-schedules';
import { PartnerScheduleTime } from '../../../../apis/models/partner-schedules/res/PartnerScheduleTime';
import { PartnerScheduleAvailableDate } from '../../../../apis/models/partner-schedules/res/PartnerScheduleAvailableDate';
import ReservationCalendar from '../../../../components/compositions/ReservationCalendar';
import ReservationTimeSelectRadioGroup from '../../../../components/compositions/ReservationTimeSelectRadioGroup';
import Button from '../../../../components/atom/Button';

export interface ReservationDateSelectModalProps {
  visible?: boolean;
  onCancel?: () => void;
  reservationId?: number;
  onDateSelect?: (reservedAt: string) => void;
}

function ReservationDateSelectModal(props: ReservationDateSelectModalProps) {
  const { visible, onCancel, reservationId, onDateSelect } = props;
  const { partner, customer } = useAuthStore();
  const { showAlert } = useAlertStore();
  const [selectedTime, setSelectedTime] = useState<string>();
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [timeBlocks, setTimeBlocks] = useState<PartnerScheduleTime[]>([]);
  const [availableDates, setAvailableDates] = useState<
    Record<string, PartnerScheduleAvailableDate>
  >({});
  const [autoSelectDateErrorTimes, setAutoSelectDateErrorTimes] = useState(0);

  const [currentCalendarVisibleMonth, setCurrentCalendarVisibleMonth] =
    useState(new Date());

  const reservationAvailableDatesQuery = useQuery({
    queryKey: [moment(currentCalendarVisibleMonth).format('YYYY-MM')],
    queryFn: async () => {
      const momentDate = moment(currentCalendarVisibleMonth);
      const year = momentDate.get('year');
      const month = momentDate.get('month') + 1;
      return fetchAvailableReservationDates({
        year,
        month,
        partnerCode: partner?.partnerCode || '',
        reservationId,
      });
    },
    onSuccess: (data) => {
      setAvailableDates(data);
      if (selectedDate) {
        const updatedDate = getEarlyAvailableDateWhenDisabled(
          selectedDate,
          data,
        );
        if (updatedDate) {
          setSelectedDate(updatedDate);
        }
      }
    },
    retry: false,
    enabled: !!partner && visible && !!reservationId,
    refetchOnWindowFocus: false,
  });

  const reservationTimeBlocksQuery = useQuery({
    queryKey: [
      moment(selectedDate).format('YYYY-MM-DD'),
      { partnerCustomerId: customer?.partnerCustomerId },
    ],
    queryFn: async () => {
      const dateParam = moment(selectedDate).format('YYYY-MM-DD');

      return fetchAvailableReservationTimes({
        targetDate: dateParam,
        partnerCode: partner?.partnerCode || '',
        reservationId,
        partnerCustomerId: customer?.partnerCustomerId,
      });
    },
    onSuccess: (data) => {
      setTimeBlocks(data);
    },
    onError: (error) => {
      const appError = error as AppError;
      if (appError.statusCode === 400 && autoSelectDateErrorTimes <= 3) {
        const momentDate = moment(selectedDate).add(1, 'd');
        setSelectedDate(momentDate.toDate());
        setAutoSelectDateErrorTimes(autoSelectDateErrorTimes + 1);
      }
    },
    retry: false,
    enabled: !!partner && visible && !!reservationId,
    refetchOnWindowFocus: false,
  });

  const renderFooter = () => {
    return (
      <div
        style={{
          padding: '16px 20px',
          boxShadow: '0px -4px 30px 0px #0000001f',
        }}
      >
        <Button
          disabled={!selectedDate || !selectedTime}
          style={{
            width: '100%',
          }}
          onClick={() => {
            showAlert(
              strings.confirmEditReservation,
              () => {
                const reservedAt =
                  moment(selectedDate).format('YYYY-MM-DD') +
                  ` ${selectedTime}`;

                if (onDateSelect) {
                  onDateSelect(reservedAt);
                }
                if (onCancel) {
                  onCancel();
                }
              },
              {
                showCancelButton: true,
              },
            );
          }}
        >
          {strings.confirm}
        </Button>
      </div>
    );
  };

  return (
    <MobileModal
      visible={visible}
      onCancel={onCancel}
      title={strings.editReservation}
      footer={renderFooter()}
    >
      <div
        style={{
          paddingLeft: 20,
          paddingBottom: 20,
          paddingRight: 20,
        }}
      >
        <ReservationCalendar
          value={selectedDate || undefined}
          loading={reservationAvailableDatesQuery.isLoading}
          availableDates={availableDates}
          onDateSelect={(date) => {
            setSelectedDate(date);
            setSelectedTime(undefined);
          }}
          onMonthChange={(date) => {
            setCurrentCalendarVisibleMonth(date);
          }}
        />
        <ReservationTimeSelectRadioGroup
          loading={reservationTimeBlocksQuery.isLoading}
          timeBlocks={timeBlocks}
          value={selectedTime}
          onTimeSelect={(timeText) => {
            setSelectedTime(timeText);
          }}
        />
      </div>
    </MobileModal>
  );
}

export default ReservationDateSelectModal;
